import React, { useEffect, useState } from 'react';
import {
  Checkbox,
  ExpandableSection,
  Form,
  FormGroup,
  FormSelect,
  FormSelectOption,
  Spinner,
  Stack,
  StackItem,
  Switch,
  Text,
  TextArea,
  TextContent,
  TextInput,
} from '@patternfly/react-core';
import { useStableAccessToken } from '@app/GranularDeploy/hooks/useStableAccessToken';
import { apiRegressionTestSchema, RegressionTestSuiteSchema } from '../helpers/apiHelpers';

export interface RegressionTestSuiteSelection {
  enabled: boolean;
  paramOverrides: Record<string, string>;
}

interface RegressionTestSuiteSelectorProps {
  selections: Record<string, RegressionTestSuiteSelection>;
  onChange: (next: Record<string, RegressionTestSuiteSelection>) => void;
}

export const RegressionTestSuiteSelector: React.FC<RegressionTestSuiteSelectorProps> = ({
  selections,
  onChange,
}) => {
  const tokenGetter = useStableAccessToken();
  const [suites, setSuites] = useState<RegressionTestSuiteSchema[] | null>(null);
  const [schemaError, setSchemaError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  useEffect(() => {
    let cancelled = false;
    apiRegressionTestSchema(tokenGetter)
      .then((result) => {
        if (cancelled) return;
        setSuites(result);
        // Seed any suite not already present in `selections` with defaults, disabled.
        const seeded = { ...selections };
        let changed = false;
        for (const suite of result) {
          if (!seeded[suite.key]) {
            seeded[suite.key] = { enabled: false, paramOverrides: { ...suite.defaults } };
            changed = true;
          }
        }
        if (changed) onChange(seeded);
      })
      .catch((err: any) => {
        if (cancelled) return;
        setSchemaError(err?.message ?? 'Failed to load regression test parameters');
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setEnabled = (suite: RegressionTestSuiteSchema, enabled: boolean) => {
    const current = selections[suite.key] ?? { enabled: false, paramOverrides: { ...suite.defaults } };
    onChange({ ...selections, [suite.key]: { ...current, enabled } });
  };

  const setFieldValue = (suite: RegressionTestSuiteSchema, fieldName: string, value: string) => {
    const current = selections[suite.key] ?? { enabled: false, paramOverrides: { ...suite.defaults } };
    onChange({
      ...selections,
      [suite.key]: { ...current, paramOverrides: { ...current.paramOverrides, [fieldName]: value } },
    });
  };

  return (
    <Stack hasGutter>
      <StackItem>
        <TextContent>
          <Text>
            Select which e2e regression suites to run against this preview environment once it's
            deployed. Expand a suite to override any of its Jenkins parameters.
          </Text>
        </TextContent>
      </StackItem>
      {schemaError && (
        <StackItem>
          <span style={{ fontSize: 13, color: '#BF2600' }}>{schemaError}</span>
        </StackItem>
      )}
      {!suites && !schemaError && <StackItem><Spinner size="sm" /></StackItem>}
      {suites?.map((suite) => {
        const selection = selections[suite.key] ?? { enabled: false, paramOverrides: { ...suite.defaults } };
        const values = selection.paramOverrides;
        return (
          <StackItem key={suite.key}>
            <Checkbox
              id={`regression-test-${suite.key}`}
              label={suite.label}
              isChecked={selection.enabled}
              onChange={(_e, checked) => setEnabled(suite, checked)}
            />
            <ExpandableSection
              toggleText={expanded[suite.key] ? 'Hide parameters' : 'Show parameters'}
              onToggle={(_e, isOpen) => setExpanded((prev) => ({ ...prev, [suite.key]: isOpen }))}
              isExpanded={!!expanded[suite.key]}
            >
              <Form>
                {suite.fields.map((field) => {
                  const value = values[field.name] ?? '';
                  const setValue = (newValue: string) => setFieldValue(suite, field.name, newValue);
                  return (
                    <FormGroup key={field.name} label={field.label} fieldId={`${suite.key}-${field.name}`}>
                      {field.type === 'choice' && (
                        <FormSelect
                          id={`${suite.key}-${field.name}`}
                          value={value}
                          onChange={(_e, newValue) => setValue(newValue)}
                        >
                          {(field.choices || []).map((choice) => (
                            <FormSelectOption key={choice} value={choice} label={choice} />
                          ))}
                        </FormSelect>
                      )}
                      {field.type === 'boolean' && (
                        <Switch
                          id={`${suite.key}-${field.name}`}
                          isChecked={value === 'true'}
                          onChange={(_e, checked) => setValue(checked ? 'true' : 'false')}
                        />
                      )}
                      {field.type === 'text' && (
                        <TextArea
                          id={`${suite.key}-${field.name}`}
                          value={value}
                          onChange={(_e, newValue) => setValue(newValue)}
                        />
                      )}
                      {field.type === 'string' && (
                        <TextInput
                          id={`${suite.key}-${field.name}`}
                          value={value}
                          onChange={(_e, newValue) => setValue(newValue)}
                        />
                      )}
                    </FormGroup>
                  );
                })}
              </Form>
            </ExpandableSection>
          </StackItem>
        );
      })}
    </Stack>
  );
};
